import i18nFactory from '@yandex-int/i18n';
import { FetchResult } from 'apollo-boost';
import { ApolloQueryResult } from 'apollo-client';
import {
    get,
    groupBy,
    isArray,
    isUndefined,
    map,
    mapValues,
    mergeWith,
    pick,
    reduce,
    sortBy,
    uniqueId
} from 'lodash';
import moment from 'moment';

import { SelectorItems } from '../../components/CampaignAndGroupSelector/Selector/Selector';
import {
    getGroupFilterCriteriaContainers,
    GroupCriteriaKey,
    StatisticsByAllGoals
} from '../../declarations/filter/filter';
import { defaultRegionsEditorData } from '../../reducers/regionsEditor';
import { cryptaAudioGenresEditorsInitialState } from '../../reducers/targetings/cryptaAudioGenres';
import { cryptaBehavioralEditorsInitialState } from '../../reducers/targetings/cryptaBehavioral';
import { cryptaGroupFamilyEditorsInitialState } from '../../reducers/targetings/cryptaGroupFamily';
import { cryptaSocialDemoEditorsInitialState } from '../../reducers/targetings/cryptaSocialDemo';
import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { getInternalType } from '../../service/CampaignHelpers/CampaignHelpers';
import { FeedCategoriesHelpers } from '../../service/FeedCategoriesHelpers/FeedCategoriesHelpers';
import { flattenContainer } from '../../service/FilterHelpers/FilterHelpers';
import { GridScreenLinkBuilder } from '../../service/GridScreenLinkBuilder';
import { GroupsHelpers } from '../../service/GroupsHelpers/GroupsHelpers';
import { mapPhraseWithMinusWordsToString } from '../../service/PhrasesHelpers/PhrasesHelpers';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import { getTagCloudTargetingId } from '../../service/TagCloudTargetingHelper/TagCloudTargetingHelper';
import {
    AudienceTargetingsType,
    AudienceTargetingsValue
} from '../../typings/audienceTargetings';
import {
    banners,
    ClientInfo,
    groups,
    NumberRangeInputValues,
    Region,
    RegionId,
    ValidationResultItem
} from '../../typings/common';
import { CryptaSegmentsByType } from '../../typings/cryptaSegments';
import { DatesIntervalEditorsState } from '../../typings/datesInterval';
import {
    BannersGroupBaseInfo,
    BannersGroupBaseInfoExtension,
    CampaignsCalcTypeFilter,
    CampaignType,
    ClientData,
    FeedFiltersGroupBaseInfo,
    GdGroupStatus,
    Group,
    GroupBaseInfo,
    GroupType
} from '../../typings/domain';
import { EditPageItemNameEditorsState } from '../../typings/editPageItemNameEditor';
import {
    Criteria,
    FilterExpression,
    FilterPartialExpression,
    GoalStatsQueryFilter,
    GroupsQueryFilter,
    StatsQueryFilter
} from '../../typings/filter';
import {
    AddSmartFilters,
    adGroupsRowFragmentForEdit,
    adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers,
    adGroupsRowFragmentForEdit_GdInternalAdGroup,
    adGroupsRowFragmentForEdit_GdSmartAdGroup,
    AdGroupTruncatedFragment,
    adsGroupRowFragment,
    CampaignBasicDataForGroupEdit_client_campaigns_rowset,
    CampaignsSuggestListQuery_client_campaigns_rowset,
    CopyAdGroupsInfoQuery,
    GdAddInternalAdGroupsItemInput,
    GdAddSmartFilterConditionInput,
    GdAddSmartFiltersInput,
    GdAdGroupPrimaryStatusInput,
    GdBidModifierType,
    GdCampaignStrategyType,
    GdCampaignType,
    GdCpmGroupTypeInput,
    GdiRecommendationType,
    GdShowConditionAutobudgetPriority,
    GdShowConditionAutobudgetPriorityInput,
    GdShowConditionType,
    GdSmartFilterConditionOperator,
    GdSmartFilterConditionOperatorInput,
    GdSmartFilterTab,
    GdSmartFilterTabInput,
    GdSmartFilterTargetFunnel,
    GdSmartFilterTargetFunnelInput,
    GdStringDictName,
    GdUpdateCpmAdGroupInput,
    GdUpdateCpmAdGroupItemInput,
    GdUpdateInternalAdGroupsItemInput,
    GdUpdatePerformanceAdGroupInput,
    GdUpdatePerformanceAdGroupItemInput,
    GdUpdateSmartFiltersInput,
    GdUpdateTextAdGroupInput,
    GdUpdateTextAdGroupItemInput,
    GroupsQueryForEdit,
    GroupsQueryForEdit_client_adGroups_rowset,
    GroupsQueryForEdit_client_feeds_rowset,
    GroupsQueryForEdit_client_feeds_rowset_categories,
    GroupsQueryForEdit_client_retargetings_rowset,
    GroupsQueryForEdit_client_showConditions_rowset,
    GroupsQueryForEdit_client_smartFilters_rowset,
    GroupsQueryForEdit_client_smartFilters_rowset_conditions,
    InterfaceLangInput,
    RecommendationsAdsGroupEditQuery_client_adGroups_rowset,
    RecommendationsGroupEditQuery,
    RetargetingConditionsQueryForGroupEdit,
    RetargetingConditionsQueryForUserProfile,
    RetargetingConditionsQueryForUserProfile_client_retargetingConditions_rowset,
    SmartFeedSchemaQuery,
    SmartFeedSchemaQuery_constants_smartFilterSchemas_fields,
    SmartFeedSchemaQuery_constants_smartFilterSchemas_fields_GdSmartFilterStringField_operators,
    UaTraitsQuery,
    UaTraitsQuery_constants_stringDicts_entries_GdStringDictEntryWithLongKey,
    UpdateSmartFilters,
    ValidationError
} from '../../typings/graphql-grid';
import { GridSourceParams } from '../../typings/grid';
import {
    DuplicateItem,
    GdAdGroup,
    GroupId,
    SmartGroupBudgetCalculation,
    StringDictsItems
} from '../../typings/groups';
import {
    ConditionsKeys,
    CpmGroupShowConditionType,
    DataForEditGroups,
    EditBaseGroupDataValue,
    EditCpmGroupDataValue,
    EditGroupAutotargeting,
    EditGroupData,
    EditGroupDataValue,
    EditGroupFilterItem,
    EditGroupKeywords,
    EditGroupRegions,
    EditInternalGroupDataValue,
    EditPerformanceGroupDataValue,
    EditTextGroupDataValue,
    Feed,
    FeedCategory,
    FeedFilterCampaign,
    FeedFilterCondition,
    FeedFilterEnumFieldParams,
    FeedFilterOperationControlValues,
    FeedFilterSchema,
    FeedFilterSchemaField,
    FeedFilterSchemaFieldType,
    FeedFilterSchemaOperator,
    Feeds,
    MassActionId,
    ProductsList,
    ProductsListItem,
    ProductsListItemRestriction,
    ProductsListItemRestrictionCondition,
    RetargetingValue,
    UpdateRetargetingConditionsForGroupsEdit
} from '../../typings/groupsEditState';
import {
    AdditionalTargetingKey,
    ApiFormatTargetingShowDates,
    TargetingShowDates
} from '../../typings/internalGroups';
import { MinusPhrasesShortLibraryItem } from '../../typings/minusPhraseLibrary';
import {
    RateCorrection,
    RateCorrectionEditorState,
    RateCorrectionType,
    RatesCorrectionsConstantsData,
    RatesCorrectionsValue
} from '../../typings/ratesCorrections';
import {
    EditPageRecommendationsData,
    RecommendationCharacter,
    RecommendationsIconType
} from '../../typings/recommendations';
import { RegionsEditorState } from '../../typings/regionsEditor';
import {
    RetargetingConditionData,
    RetargetingEditorState
} from '../../typings/retargeting';
import {
    CryptaAudioGenresEditorsState,
    CryptaBehavioralEditorsState,
    CryptaGroupFamilyEditorsState,
    CryptaSocialDemoEditorsState,
    InterestsEditorsState,
    MinusPhraseLibraryEditorState
} from '../../typings/state';
import { TagCloudTargetingsState } from '../../typings/tagCloudTargeting';
import { UserProfileRetargetingCondition } from '../../typings/userProfileRetargeting';
import {
    makeAudienceCollectionForApi,
    makeAudienceCollectionFromApi
} from '../banners-edit/api-data-mappers';
import { mapRowToCampaignTruncatedBaseInfo } from '../campaign';
import {
    getHrefForDomainFromApiFormat,
    getValueFromApiFormat,
    mapCampaignRowTypeToCampaignType,
    mapGroupRowTypeToGroupType,
    mapJoinTypeToApiFormat,
    mapStatsDataToStats,
    mapTargetingModeToApiFormat
} from '../common/common';
import {
    FilterCriteriaMappings,
    getStrictExpressions,
    textToGroupFilterExpression
} from '../filter/filter';
import { mapRowGoals } from '../goal/goal';
import { mapTagCloudTargetingValue } from '../internalGroup';
import { mapDisplayPageBlockFromApiFormat } from '../placementsMap/placementsMap';
import {
    getAllowedRateCorrectionsDataForGroup,
    mapEditorRateCorrectionsToRateCorrections,
    mapRateCorrectionsCpmBannerData,
    mapRateCorrectionsData,
    mapRatesCorrectionsConstantsFromApiFormat,
    mapRatesCorrectionsValueToApiFormatForGroup
} from '../rateCorrections/rateCorrections';
import {
    isRecommendationSupported,
    isRecommendationSupportedOnEditGroupPage,
    isRecommendationSupportedOnGridByGroupType,
    mapBaseRecommendationInfoFromApiFormat,
    mapContextRecommendationFromApiFormat
} from '../recommendation';
import {
    mapRetargetingConditionFromApiFormat,
    mapRetargetingEditorValueToGroupRetargetingValue
} from '../retargeting/retargeting';
import {
    mapAudienceTargetingsToRetargetingConditionsApiFormat,
    mapUserProfileRetargetingConditionFromApiFormat
} from '../retargetingCondition/retargetingCondition';

import * as keyset from './group.i18n';

const i18n = i18nFactory(keyset);

const mapGroupStatusFromApiFormat = (
    status: adsGroupRowFragment['status']
): GdGroupStatus => {
    const {
        primaryStatus,
        primaryStatusDesc,
        moderationStatus,
        bsEverSynced,
        blGenerationStatus
    } = status;
    return {
        primaryStatus,
        primaryStatusDesc: getValueFromApiFormat(primaryStatusDesc),
        moderationStatus,
        bsEverSynced,
        blGenerationStatus: getValueFromApiFormat(blGenerationStatus)
    };
};

const mapRowToGroupBaseInfo = (
    params: GridSourceParams,
    client: ClientData
): ((row: adsGroupRowFragment) => GroupBaseInfo) => (
    row: adsGroupRowFragment
): GroupBaseInfo => {
    return {
        id: String(row.id),
        campaign: mapRowToCampaignTruncatedBaseInfo(
            row.campaign,
            client,
            params
        ),
        name: row.name,
        groupUrl: GridScreenLinkBuilder.getContextTypeRelevantUrl(
            groups,
            banners,
            {
                login: params.login,
                campaignsIds: [String(row.campaign.id)],
                contextIds: [String(row.id)]
            },
            params
        ),
        access: row.access,
        status: mapGroupStatusFromApiFormat(row.status),
        regionIds: row.regionsInfo.regionIds,
        minusKeywords: row.minusKeywords
    };
};

export const mapRowsToGroupsForCopy = (
    items: CampaignsSuggestListQuery_client_campaigns_rowset[],
    campaignType?: CampaignType
): SelectorItems[] =>
    items.reduce(
        (
            acc: SelectorItems[],
            item: CampaignsSuggestListQuery_client_campaigns_rowset
        ) => {
            const campaignTypeCurrent = mapCampaignRowTypeToCampaignType(
                item.type
            );

            if (
                campaignTypeCurrent === campaignType ||
                campaignType === undefined
            ) {
                const campaign: SelectorItems = {
                    id: String(item.id),
                    name: item.name,
                    type: campaignTypeCurrent
                };

                acc.push(campaign);
            }

            return acc;
        },
        []
    );

const mapRowsToBannersGroupBaseInfo = (
    params: GridSourceParams,
    client: ClientData
): ((row: adsGroupRowFragment) => BannersGroupBaseInfo) => (
    row: adsGroupRowFragment
): BannersGroupBaseInfo => {
    const groupBaseInfo = mapRowToGroupBaseInfo(params, client)(row);
    const groupHref = getGroupHrefFromApiFormat(row);

    return {
        ...groupBaseInfo,
        ...groupHref
    };
};

export function mapRowsToBannersGroupsBaseInfo(
    groupRows: adsGroupRowFragment[],
    client: ClientData,
    params: GridSourceParams
): BannersGroupBaseInfo[] {
    return groupRows.map(mapRowsToBannersGroupBaseInfo(params, client));
}

export function mapRowsToGroupsTruncatedBaseInfo(
    row: AdGroupTruncatedFragment,
    client: ClientData,
    params: GridSourceParams
): GroupBaseInfo {
    const campaign = mapRowToCampaignTruncatedBaseInfo(
        row.campaign,
        client,
        params
    );

    return {
        id: String(row.id),
        campaign: campaign,
        name: row.name,
        access: row.access,
        groupUrl: GridScreenLinkBuilder.getContextTypeRelevantUrl(
            groups,
            banners,
            {
                login: params.login,
                campaignsIds: [String(row.campaign.id)],
                contextIds: [String(row.id)]
            },
            params
        ),
        status: mapGroupStatusFromApiFormat(row.status),
        regionIds: row.regionsInfo.regionIds,
        minusKeywords: row.minusKeywords,
        type: mapGroupRowTypeToGroupType(row.type)
    };
}

export function mapRowsToFeedFilterGroupTruncatedInfo(
    row: AdGroupTruncatedFragment,
    client: ClientData,
    params: GridSourceParams
): FeedFiltersGroupBaseInfo {
    const groupBaseInfo = mapRowsToGroupsTruncatedBaseInfo(row, client, params);

    if (row.__typename === 'GdSmartAdGroup') {
        return {
            ...groupBaseInfo,
            feedId: row.feedId
        };
    }

    throw new Error('Cannot map not smart feed filter group');
}

export function mapRowsToGroups(
    groupRows: adsGroupRowFragment[],
    client: ClientData,
    params: GridSourceParams
): Group[] {
    return groupRows.map(row => {
        const groupBaseInfo = mapRowToGroupBaseInfo(params, client)(row);
        const rowGoalStats = row.goalStats || [];
        const recommendations = (row.recommendations || [])
            .filter(
                recommendation =>
                    // isRecommendationSupportedOnGridByGroupType - рекомендации которые поддерживаются только для определенных групп
                    isRecommendationSupported(recommendation.type) ||
                    isRecommendationSupportedOnGridByGroupType(
                        recommendation.type,
                        row.type
                    )
            )
            .map(mapContextRecommendationFromApiFormat);

        return {
            ...groupBaseInfo,
            access: row.access,
            type: mapGroupRowTypeToGroupType(row.type),
            campaignType: groupBaseInfo.campaign.type,
            stats: mapStatsDataToStats(row.stats),
            goalStats: mapRowGoals(rowGoalStats),
            recommendations: {
                data:
                    recommendations.length !== 0 ? recommendations : undefined,
                recommendationsIconType: recommendations.some(
                    recommendation =>
                        recommendation.character ===
                        RecommendationCharacter.attention
                )
                    ? RecommendationsIconType.red
                    : RecommendationsIconType.yellow
            },
            minusKeywords: row.minusKeywords,
            regionIds: row.regionsInfo.regionIds,
            availableShowConditionTypes: row.availableShowConditionTypes
        };
    });
}

const mappings = new FilterCriteriaMappings<
    GroupsQueryFilter & StatsQueryFilter & GoalStatsQueryFilter
>();
const { toNumber, toEnum } = mappings.valueMappings;

const groupCriteriaMappings: {
    [key in GroupCriteriaKey]: (
        expression: FilterExpression
    ) => GroupsQueryFilter
} = {
    id: mappings.includes('adGroupIdIn', 'adGroupIdNotIn'),
    name: mappings.contains('nameContains', 'nameNotContains'),
    nameMinusPhrasePacks: mappings.contains(
        'mwPackNameContains',
        'mwPackNameNotContains'
    ),
    status: mappings.equality(
        'primaryStatusIn',
        toEnum(GdAdGroupPrimaryStatusInput)
    ),
    cost: mappings.comparison('minCost', 'maxCost', toNumber),
    revenue: mappings.comparison('minRevenue', 'maxRevenue', toNumber),
    profitability: mappings.comparison(
        'minProfitability',
        'maxProfitability',
        toNumber
    ),
    avgGoalCost: (expression: FilterExpression): GroupsQueryFilter => {
        if (
            expression.additionalCriteriaKey &&
            expression.additionalCriteriaKey !== StatisticsByAllGoals
        ) {
            return mappings.comparison(
                'minCostPerAction',
                'maxCostPerAction',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison(
            'minAvgGoalCost',
            'maxAvgGoalCost',
            toNumber
        )(expression);
    },
    avgClickCost: mappings.comparison(
        'minAvgClickCost',
        'maxAvgClickCost',
        toNumber
    ),
    avgCpmCost: mappings.comparison('minCpmPrice', 'maxCpmPrice', toNumber),
    recommendations: mappings.equality(
        'recommendations',
        toEnum(GdiRecommendationType)
    ),
    shows: mappings.comparison('minShows', 'maxShows', toNumber),
    clicks: mappings.comparison('minClicks', 'maxClicks', toNumber),
    ctr: mappings.comparison('minCtr', 'maxCtr', toNumber),
    goals: (expression: FilterExpression): GroupsQueryFilter => {
        if (
            expression.additionalCriteriaKey &&
            expression.additionalCriteriaKey !== StatisticsByAllGoals
        ) {
            return mappings.comparison(
                'minGoals',
                'maxGoals',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison('minGoals', 'maxGoals', toNumber)(
            expression
        );
    },
    conversionRate: (expression: FilterExpression): GroupsQueryFilter => {
        if (
            expression.additionalCriteriaKey &&
            expression.additionalCriteriaKey !== StatisticsByAllGoals
        ) {
            return mappings.comparison(
                'minConversionRate',
                'maxConversionRate',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison(
            'minConversionRate',
            'maxConversionRate',
            toNumber
        )(expression);
    },
    bounceRate: mappings.comparison('minBounceRate', 'maxBounceRate', toNumber),
    libraryMinusPhrasePacks: mappings.includes(
        'libraryMwIdIn',
        'libraryMwIdNotIn'
    )
};

const expressionToGroupsQueryFilter = (
    expression: FilterExpression
): GroupsQueryFilter => {
    return groupCriteriaMappings[expression.criteriaKey](expression);
};

export const mapGroupFilterToApiFormat = (
    expressions: FilterPartialExpression[],
    clientInfo: ClientInfo,
    calcType?: CampaignsCalcTypeFilter
): GroupsQueryFilter => {
    const criteria: Criteria[] = [];

    getGroupFilterCriteriaContainers(
        clientInfo,
        calcType === CampaignsCalcTypeFilter.CPM
    ).forEach(({ criteria: c }) => criteria.push(...flattenContainer(c)));

    return getStrictExpressions(
        expressions,
        criteria,
        textToGroupFilterExpression
    )
        .map(expressionToGroupsQueryFilter)
        .reduce(
            (obj, otherObj) =>
                mergeWith(obj, otherObj, (objValue, srcValue) => {
                    if (isArray(objValue)) {
                        return objValue.concat(srcValue);
                    }

                    return undefined;
                }),
            {}
        );
};

export const isGroupEditable = (group: GroupBaseInfo): boolean => {
    return group.access.canEdit;
};

export const getGroupHrefFromApiFormat = (
    group: adsGroupRowFragment
): BannersGroupBaseInfoExtension => {
    switch (group.__typename) {
        case 'GdDynamicAdGroup':
            return {
                mainDomain: getHrefForDomainFromApiFormat(group.mainDomain)
            };
        case 'GdMobileContentAdGroup':
            return {
                storeHref: getValueFromApiFormat(group.storeHref)
            };
        default:
            return {};
    }
};

export const getNewGroupName = (): string => {
    return i18n('newGroup');
};

type GroupShowConditionsData = {
    keywords: EditGroupKeywords[];
    autotargeting: EditGroupAutotargeting;
};

const getShowConditionsEmptyData = (): GroupShowConditionsData => {
    return {
        keywords: [],
        autotargeting: {
            isActive: false
        }
    };
};

function mapTextGroupData(
    groupRow: adGroupsRowFragmentForEdit,
    showConditionsData: GroupShowConditionsData,
    groupRetargetingConditions: Array<RetargetingValue>,
    regionTree: Region[],
    availableBidModifiersTypes: Array<GdBidModifierType> = []
): EditTextGroupDataValue {
    const selectedMap = RegionsHelpers.getSelectedMapFromRawRegionData(
        regionTree,
        groupRow.regionsInfo.regionIds
    );

    return {
        campaignType: CampaignType.TEXT,
        groupType: mapGroupRowTypeToGroupType(groupRow.type),
        name: groupRow.name,
        keywords: showConditionsData.keywords,
        minusKeywords: groupRow.minusKeywords || [],
        selectedLibraryList:
            groupRow.libraryMinusKeywordsPacks.map(item => {
                return { id: String(item.id), name: item.name };
            }) || [],
        autotargeting: showConditionsData.autotargeting,
        retargeting: groupRetargetingConditions,
        rateCorrections: mapRateCorrectionsData(
            groupRow.bidModifiers,
            availableBidModifiersTypes
        ),
        generalRate: undefined,
        regions: {
            selectedMap: selectedMap,
            regionIds: groupRow.regionsInfo.regionIds
        }
    };
}

function mapCpmGroupData(
    groupRow: adGroupsRowFragmentForEdit,
    showConditionsData: GroupShowConditionsData,
    regionTree: Region[],
    availableBidModifiersTypes: Array<GdBidModifierType> = [],
    generalRate?: number,
    retargetingCondition?: UserProfileRetargetingCondition,
    productsList?: Record<string, ProductsListItem>
): EditCpmGroupDataValue {
    const selectedMap = RegionsHelpers.getSelectedMapFromRawRegionData(
        regionTree,
        groupRow.regionsInfo.regionIds
    );
    const { rateCurrentType, rateCorrections } = getCurrentRateTypeAndLists({
        productRestrictionId: groupRow.productRestrictionId,
        rateCorrectionsFromApi: groupRow.bidModifiers,
        productsList: productsList
    });
    const videoGoals =
        (groupRow.__typename === 'GdCpmVideoAdGroup' ||
            groupRow.__typename === 'GdCpmOutdoorAdGroup' ||
            groupRow.__typename === 'GdCpmIndoorAdGroup' ||
            groupRow.__typename === 'GdCpmAudioAdGroup') &&
        'videoGoals' in groupRow
            ? groupRow.videoGoals
            : [];

    const groupType = mapGroupRowTypeToGroupType(groupRow.type);

    return {
        campaignType: CampaignType.CPM_BANNER,
        groupType,
        name: groupRow.name,
        productRestrictionId: String(groupRow.productRestrictionId) || '',
        keywords: showConditionsData.keywords,
        autotargeting: showConditionsData.autotargeting,
        showConditionType: groupRow.availableShowConditionTypes.some(
            conditionType => conditionType === GdShowConditionType.USER_PROFILE
        )
            ? CpmGroupShowConditionType.USER_PROFILE
            : CpmGroupShowConditionType.KEYWORD,
        rateCorrections,
        rateCurrentType,
        generalRate,
        regions: {
            selectedMap: selectedMap,
            regionIds: groupRow.regionsInfo.regionIds
        },
        outdoorMap:
            groupType === GroupType.CPM_OUTDOOR && 'pageBlocks' in groupRow
                ? {
                      pageBlocks: mapDisplayPageBlockFromApiFormat(
                          groupRow.pageBlocks
                      )
                  }
                : undefined,
        indoorMap:
            groupType === GroupType.CPM_INDOOR && 'pageBlocks' in groupRow
                ? {
                      pageBlocks: mapDisplayPageBlockFromApiFormat(
                          groupRow.pageBlocks
                      )
                  }
                : undefined,
        audienceCollection: videoGoals.map(makeAudienceCollectionFromApi),
        audienceTargetingsTemplateName: retargetingCondition
            ? retargetingCondition.name
            : '',
        audienceTargetings: retargetingCondition
            ? retargetingCondition.audienceTargetings
            : {}
    };
}

export function getCurrentRateTypeAndLists(parameters: {
    productRestrictionId: string | null;
    rateCorrectionsFromApi: Array<
        adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers
    > | null;
    productsList?: Record<string, ProductsListItem>;
}): Pick<EditCpmGroupDataValue, 'rateCurrentType' | 'rateCorrections'> {
    const {
        productRestrictionId,
        rateCorrectionsFromApi,
        productsList
    } = parameters;
    const rateCorrectionsByType =
        productsList && productsList[CampaignType.CPM_BANNER];
    const rateCorrectionByRestrictions =
        rateCorrectionsByType && rateCorrectionsByType.restrictions
            ? rateCorrectionsByType.restrictions
            : [];
    const rateCorrectionsLists = rateCorrectionByRestrictions.find(
        (restrict: ProductsListItemRestriction) =>
            restrict.id === productRestrictionId + ''
    );
    const settingsForRate = rateCorrectionsLists
        ? rateCorrectionsLists.conditions
              .filter(
                  (item: ProductsListItemRestrictionCondition) =>
                      item.name === ConditionsKeys.correction
              )
              .map(
                  (item: ProductsListItemRestrictionCondition) =>
                      item.nestedConditions
              )[0]
        : [];
    const rateCorrections = mapRateCorrectionsCpmBannerData(
        rateCorrectionsFromApi,
        settingsForRate
    );
    const keyRate =
        Object.keys(rateCorrections).shift() || RateCorrectionType.retargeting;
    const rateCurrentType =
        (rateCorrections[keyRate] && rateCorrections[keyRate].type) ||
        RateCorrectionType.retargeting;

    return {
        rateCurrentType,
        rateCorrections
    };
}

function mapSmartGroupData(
    groupRow: adGroupsRowFragmentForEdit_GdSmartAdGroup,
    smartFilters: Array<EditGroupFilterItem>,
    regionTree: Region[],
    availableBidModifiersTypes: Array<GdBidModifierType>
): EditPerformanceGroupDataValue {
    const selectedMap = RegionsHelpers.getSelectedMapFromRawRegionData(
        regionTree,
        groupRow.regionsInfo.regionIds
    );

    return {
        campaignType: CampaignType.PERFORMANCE,
        groupType: mapGroupRowTypeToGroupType(groupRow.type),
        name: groupRow.name,
        regions: {
            selectedMap: selectedMap,
            regionIds: groupRow.regionsInfo.regionIds
        },
        minusKeywords: groupRow.minusKeywords || [],
        selectedLibraryList:
            groupRow.libraryMinusKeywordsPacks.map(item => {
                return { id: String(item.id), name: item.name };
            }) || [],
        rateCorrections: mapRateCorrectionsData(
            groupRow.bidModifiers,
            availableBidModifiersTypes
        ),
        hrefParams: groupRow.trackingParams || '',
        bannerHeadersFeedField:
            getValueFromApiFormat(groupRow.fieldToUseAsName) || '',
        bannerTextFeedField:
            getValueFromApiFormat(groupRow.fieldToUseAsBody) || '',
        feedsPopupIsOpen: false,
        searchFeedFieldValue: '',
        selectedFeedId: '',
        feedId: String(groupRow.feedId),
        filters: smartFilters || []
    };
}

function mapInternalGroupData(
    groupRow: adGroupsRowFragmentForEdit_GdInternalAdGroup,
    regionTree: Region[],
    groupType: GdCampaignType
): EditInternalGroupDataValue {
    const type = mapCampaignRowTypeToCampaignType(groupType);
    const internalGroupType = getInternalType(type);

    if (!internalGroupType) {
        throw new Error('not internal group type');
    }

    const selectedMap = RegionsHelpers.getSelectedMapFromRawRegionData(
        regionTree,
        groupRow.regionsInfo.regionIds
    );

    return {
        campaignType: internalGroupType,
        groupType: mapGroupRowTypeToGroupType(groupRow.type),
        name: groupRow.name,
        regions: {
            selectedMap: selectedMap,
            regionIds: groupRow.regionsInfo.regionIds
        },
        targetingYandexUids: groupRow.targetingYandexUids
            ? {
                  joinType: groupRow.targetingYandexUids.joinType,
                  targetingMode: groupRow.targetingYandexUids.targetingMode,
                  value: groupRow.targetingYandexUids.value
                      ? groupRow.targetingYandexUids.value.join(', ')
                      : ''
              }
            : undefined,
        targetingOsNames: groupRow.targetingOsNames
            ? {
                  joinType: groupRow.targetingOsNames.joinType,
                  targetingMode: groupRow.targetingOsNames.targetingMode,
                  value: groupRow.targetingOsNames.value || []
              }
            : undefined,
        targetingOsFamilies: groupRow.targetingOsFamilies
            ? {
                  joinType: groupRow.targetingOsFamilies.joinType,
                  targetingMode: groupRow.targetingOsFamilies.targetingMode,
                  value: groupRow.targetingOsFamilies.value || []
              }
            : undefined,
        targetingBrowserNames: groupRow.targetingBrowserNames
            ? {
                  joinType: groupRow.targetingBrowserNames.joinType,
                  targetingMode: groupRow.targetingBrowserNames.targetingMode,
                  value: groupRow.targetingBrowserNames.value || []
              }
            : undefined,
        targetingBrowserEngines: groupRow.targetingBrowserEngines
            ? {
                  joinType: groupRow.targetingBrowserEngines.joinType,
                  targetingMode: groupRow.targetingBrowserEngines.targetingMode,
                  value: groupRow.targetingBrowserEngines.value || []
              }
            : undefined,
        targetingDeviceVendors: groupRow.targetingDeviceVendors
            ? {
                  joinType: groupRow.targetingDeviceVendors.joinType,
                  targetingMode: groupRow.targetingDeviceVendors.targetingMode,
                  value: groupRow.targetingDeviceVendors.value || []
              }
            : undefined,
        targetingDeviceNames: groupRow.targetingDeviceNames
            ? {
                  joinType: groupRow.targetingDeviceNames.joinType,
                  targetingMode: groupRow.targetingDeviceNames.targetingMode,
                  value: groupRow.targetingDeviceNames.value
                      ? groupRow.targetingDeviceNames.value.join(', ')
                      : ''
              }
            : undefined,
        targetingInterfaceLangs: groupRow.targetingInterfaceLangs
            ? {
                  joinType: groupRow.targetingInterfaceLangs.joinType,
                  targetingMode: groupRow.targetingInterfaceLangs.targetingMode,
                  value: groupRow.targetingInterfaceLangs.value || []
              }
            : undefined,
        targetingQueryReferers: groupRow.targetingQueryReferers
            ? {
                  joinType: groupRow.targetingQueryReferers.joinType,
                  targetingMode: groupRow.targetingQueryReferers.targetingMode,
                  value: groupRow.targetingQueryReferers.value
                      ? groupRow.targetingQueryReferers.value.join(', ')
                      : ''
              }
            : undefined,
        targetingUserAgents: groupRow.targetingUserAgents
            ? {
                  joinType: groupRow.targetingUserAgents.joinType,
                  targetingMode: groupRow.targetingUserAgents.targetingMode,
                  value: groupRow.targetingUserAgents.value
                      ? groupRow.targetingUserAgents.value.join(', ')
                      : ''
              }
            : undefined,
        targetingInternalNetwork: groupRow.targetingInternalNetwork
            ? {
                  joinType: groupRow.targetingInternalNetwork.joinType,
                  targetingMode: groupRow.targetingInternalNetwork.targetingMode
              }
            : undefined,
        targetingIsTablet: groupRow.targetingIsTablet
            ? {
                  joinType: groupRow.targetingIsTablet.joinType,
                  targetingMode: groupRow.targetingIsTablet.targetingMode
              }
            : undefined,
        targetingIsBrowser: groupRow.targetingIsBrowser
            ? {
                  joinType: groupRow.targetingIsBrowser.joinType,
                  targetingMode: groupRow.targetingIsBrowser.targetingMode
              }
            : undefined,
        targetingIsTouch: groupRow.targetingIsTouch
            ? {
                  joinType: groupRow.targetingIsTouch.joinType,
                  targetingMode: groupRow.targetingIsTouch.targetingMode
              }
            : undefined,
        targetingIsMobile: groupRow.targetingIsMobile
            ? {
                  joinType: groupRow.targetingIsMobile.joinType,
                  targetingMode: groupRow.targetingIsMobile.targetingMode
              }
            : undefined,
        targetingShowDates: groupRow.targetingShowDates
            ? {
                  joinType: groupRow.targetingShowDates.joinType,
                  targetingMode: groupRow.targetingShowDates.targetingMode,
                  value: groupRow.targetingShowDates.value
                      ? groupRow.targetingShowDates.value.sort((a, b) =>
                            moment(a).diff(moment(b))
                        )
                      : []
              }
            : undefined,
        startTime: getValueFromApiFormat(groupRow.startTime),
        finishTime: getValueFromApiFormat(groupRow.finishTime),
        level: groupRow.level || '0',
        isAdditionalTargetingInternalOpen: false
    };
}

function makeInternalGroupEmptyData(
    groupType: CampaignType
): EditInternalGroupDataValue {
    const internalGroupType = getInternalType(groupType);
    if (!internalGroupType) {
        throw new Error('not internal group type');
    }

    return {
        campaignType: internalGroupType,
        groupType: GroupType.INTERNAL,
        name: i18n('newGroup'),
        regions: {
            selectedMap: {},
            regionIds: []
        },
        targetingYandexUids: undefined,
        targetingInternalNetwork: undefined,
        level: '0',
        isAdditionalTargetingInternalOpen: false
    };
}

function makePerformanceGroupEmptyData(
    regionTree: Region[],
    allowedRateCorrectionsData: Record<GroupType, Array<GdBidModifierType>>,
    defaultRegionIds: RegionId[]
): EditPerformanceGroupDataValue {
    const rateCorrections = allowedRateCorrectionsData[GroupType.PERFORMANCE];

    return {
        campaignType: CampaignType.PERFORMANCE,
        groupType: GroupType.PERFORMANCE,
        name: getNewGroupName(),
        regions: {
            selectedMap: RegionsHelpers.getSelectedMapFromRawRegionData(
                regionTree,
                defaultRegionIds
            ),
            regionIds: defaultRegionIds
        },
        minusKeywords: [],
        selectedLibraryList: [],
        rateCorrections: mapRateCorrectionsData(null, rateCorrections),
        hrefParams: '',
        filters: [],
        feedsPopupIsOpen: false,
        searchFeedFieldValue: '',
        selectedFeedId: '',
        feedId: '',
        bannerHeadersFeedField: '',
        bannerTextFeedField: ''
    };
}

function getCpmGroupDefaultRestrictionData(productsList: ProductsList): string {
    const defaultRestrictionId = get(productsList, [
        CampaignType.CPM_BANNER,
        'restrictions',
        0,
        'id'
    ]);

    if (defaultRestrictionId === undefined) {
        throw new Error('Для продукта CPM_BANNER нет данных');
    }

    return defaultRestrictionId;
}

function makeCpmGroupEmptyData(
    regionTree: Region[],
    defaultRegionIds: RegionId[],
    productsList: ProductsList
): EditCpmGroupDataValue {
    const productRestrictionId = getCpmGroupDefaultRestrictionData(
        productsList
    );
    const { rateCurrentType, rateCorrections } = getCurrentRateTypeAndLists({
        productRestrictionId: productRestrictionId,
        rateCorrectionsFromApi: [],
        productsList: productsList
    });
    const showConditionsData = getShowConditionsEmptyData();

    return {
        name: getNewGroupName(),
        campaignType: CampaignType.CPM_BANNER,
        groupType: GroupType.CPM_BANNER,
        autotargeting: showConditionsData.autotargeting,
        keywords: showConditionsData.keywords,
        showConditionType: CpmGroupShowConditionType.USER_PROFILE,
        rateCurrentType,
        rateCorrections,
        regions: {
            selectedMap: RegionsHelpers.getSelectedMapFromRawRegionData(
                regionTree,
                defaultRegionIds
            ),
            regionIds: defaultRegionIds
        },
        outdoorMap: {
            pageBlocks: []
        },
        indoorMap: {
            pageBlocks: []
        },
        generalRate: undefined,
        selectedLibraryList: [],
        audienceTargetings: {},
        minusKeywords: [],
        productRestrictionId,
        audienceTargetingsTemplateName: ''
    };
}

function mapBaseGroupData(
    groupRow: adGroupsRowFragmentForEdit
): EditBaseGroupDataValue {
    return {
        name: groupRow.name,
        campaignType: undefined,
        groupType: mapGroupRowTypeToGroupType(groupRow.type)
    };
}

const getShowConditionsData = (
    showConditionsData: GroupsQueryForEdit_client_showConditions_rowset[]
): Record<GroupId, GroupShowConditionsData> => {
    return showConditionsData.reduce((acc, item) => {
        if (acc[item.adGroupId] === undefined) {
            acc[item.adGroupId] = getShowConditionsEmptyData();
        }

        switch (item.__typename) {
            case 'GdKeyword':
                acc[item.adGroupId].keywords.push({
                    id: item.id,
                    phrase: item.keyword,
                    minusWords: item.minusKeywords
                });
                break;

            case 'GdRelevanceMatch':
                acc[item.adGroupId].autotargeting = {
                    id: item.id,
                    isActive: true
                };
                break;

            default:
                break;
        }

        return acc;
    }, {});
};

export const makeEmptySmartFilterItem = (
    campaign?: FeedFilterCampaign
): EditGroupFilterItem => {
    return {
        autobudgetPriority: GdShowConditionAutobudgetPriority.MEDIUM,
        type: GdSmartFilterTargetFunnel.NEW_AUDITORY,
        name: '',
        id: '',
        tab: GdSmartFilterTab.ALL_PRODUCTS,
        conditions: [],
        feedCategories: [],
        vendors: '',
        priceRange: [0, 0],
        categoryIds: [],
        campaign: campaign || {},
        isAvailable: false
    };
};

export const mapAvailableConditionFromApiResponse = (
    conditions: Array<FeedFilterCondition>
): boolean => {
    const availableCondition = conditions.find(
        condition => condition.field === 'available'
    );

    return Boolean(availableCondition && availableCondition.arguments);
};

const mapVendorsConditionFromApiResponse = (
    conditions: Array<FeedFilterCondition>
): string => {
    const vendorCondition = conditions.find(
        condition => condition.field === 'vendor'
    );

    return (vendorCondition && vendorCondition.arguments.join(',')) || '';
};

const mapPriceConditionFromApiResponse = (
    conditions: Array<FeedFilterCondition>
): NumberRangeInputValues => {
    const priceCondition = conditions.find(
        condition => condition.field === 'price'
    );

    const priceRanges =
        priceCondition &&
        (priceCondition.arguments as Array<NumberRangeInputValues>);

    return (priceRanges && priceRanges[0]) || [0, 0];
};

const mapCategoryIdConditionFromApiResponse = (
    conditions: Array<FeedFilterCondition>
): Array<string> => {
    const categoryIdCondition = conditions.find(
        condition => condition.field === 'categoryId'
    );

    return (categoryIdCondition ? categoryIdCondition.arguments : []) as Array<
        string
    >;
};

export const mapCrossTabConditionsFromApiResponse = (
    conditions: Array<FeedFilterCondition>,
    tab: GdSmartFilterTab
): {
    vendors: string;
    priceRange: NumberRangeInputValues;
    categoryIds: Array<string>;
    conditions: Array<FeedFilterCondition>;
} => {
    const CROSS_TAB_CONDITION_FIELDS = [
        'price',
        'vendor',
        'categoryId',
        'available'
    ];

    if (tab === GdSmartFilterTab.TREE) {
        return {
            vendors: mapVendorsConditionFromApiResponse(conditions),
            priceRange: mapPriceConditionFromApiResponse(conditions),
            categoryIds: mapCategoryIdConditionFromApiResponse(conditions),
            conditions: conditions.filter(
                condition =>
                    !CROSS_TAB_CONDITION_FIELDS.includes(condition.field)
            )
        };
    }

    return {
        vendors: '',
        priceRange: [0, 0],
        categoryIds: [],
        conditions: conditions.filter(
            condition => condition.field !== 'available'
        )
    };
};

const getGroupsFilterCampaignInfo = (
    groupsList: Array<GroupsQueryForEdit_client_adGroups_rowset>
): Record<GroupId, FeedFilterCampaign> => {
    return groupsList.reduce((acc, group) => {
        const { id, campaign } = group;
        const { type: strategy, platform } = campaign.flatStrategy;

        if (!acc[id]) {
            acc[id] = { strategy, platform };
        }

        return acc;
    }, {});
};

export const getSmartFiltersData = (
    smartFilterData: Array<GroupsQueryForEdit_client_smartFilters_rowset>,
    campaigns: Record<GroupId, FeedFilterCampaign>
): Record<GroupId, Array<EditGroupFilterItem>> => {
    const accumulator: Record<GroupId, Array<EditGroupFilterItem>> = {};

    return smartFilterData.reduce((acc, item) => {
        const {
            adGroupId,
            id,
            targetFunnel,
            name,
            priceCpa,
            priceCpc,
            autobudgetPriority,
            conditions,
            tab
        } = item;

        if (!acc[adGroupId]) {
            acc[adGroupId] = [];
        }

        const resultConditions = conditions.map(
            mapSmartFiltersConditionFromApiResponse
        );

        acc[adGroupId].push({
            ...mapCrossTabConditionsFromApiResponse(resultConditions, tab),
            isAvailable: mapAvailableConditionFromApiResponse(resultConditions),
            type: targetFunnel,
            id,
            name,
            priceCpa: Number(priceCpa) || undefined,
            priceCpc: Number(priceCpc) || undefined,
            autobudgetPriority:
                autobudgetPriority || GdShowConditionAutobudgetPriority.MEDIUM,
            feedCategories: [],
            tab,
            campaign: campaigns[adGroupId]
        });
        return acc;
    }, accumulator);
};

export const mapSmartFiltersConditionArgumentsFromApiResponse = (
    stringValue: string,
    field: string,
    operator: GdSmartFilterConditionOperator
): FeedFilterOperationControlValues => {
    const parsedValues = JSON.parse(stringValue);

    if (operator === GdSmartFilterConditionOperator.RANGE) {
        return parsedValues.map((item: string) => item.split('-').map(Number));
    }

    return parsedValues;
};

export const makeSmartFilterCondition = (
    field: string,
    operator: GdSmartFilterConditionOperator,
    values: FeedFilterOperationControlValues
): FeedFilterCondition => {
    return {
        id: uniqueId('smartFilterCondition'),
        field,
        arguments: values,
        operator
    };
};

export const mapSmartFiltersConditionFromApiResponse = (
    condition: GroupsQueryForEdit_client_smartFilters_rowset_conditions
): FeedFilterCondition => {
    const { field, operator, stringValue } = condition;
    const values = mapSmartFiltersConditionArgumentsFromApiResponse(
        stringValue,
        field,
        operator
    );

    return makeSmartFilterCondition(field, operator, values);
};

export interface RetargetingData {
    priceContext: number;
    retargetings: Array<RetargetingValue>;
}

export const getRetargetingData = (
    retargetingData: GroupsQueryForEdit_client_retargetings_rowset[]
): Record<GroupId, RetargetingData> => {
    return retargetingData.reduce(
        (
            acc: Record<GroupId, RetargetingData>,
            item: GroupsQueryForEdit_client_retargetings_rowset
        ) => {
            if (acc[item.adGroupId] === undefined) {
                acc[item.adGroupId] = {
                    priceContext: Number(item.priceContext),
                    retargetings: []
                };
            }

            acc[item.adGroupId].retargetings.push({
                id: item.retargetingConditionId,
                retargetingId: item.retargetingId
            });

            return acc;
        },
        {}
    );
};

export const getUserProfileRetargetingData = (
    retargetingData?: ApolloQueryResult<
        RetargetingConditionsQueryForUserProfile
    >
): Record<GroupId, UserProfileRetargetingCondition> => {
    if (!retargetingData) {
        return {};
    }

    return retargetingData.data.client.retargetingConditions.rowset.reduce(
        (
            acc: Record<GroupId, UserProfileRetargetingCondition>,
            item: RetargetingConditionsQueryForUserProfile_client_retargetingConditions_rowset
        ) => {
            item.adGroupIds.forEach(adGroupId => {
                acc[
                    adGroupId
                ] = mapUserProfileRetargetingConditionFromApiFormat(item);
            });

            return acc;
        },
        {}
    );
};

export const mapGroupsForEditQueryResultToQueryResponse = (
    login: string,
    result: ApolloQueryResult<GroupsQueryForEdit>,
    regionTree: Region[],
    isDeviceModifiersAllowed: boolean,
    userProfileRetargetings?: ApolloQueryResult<
        RetargetingConditionsQueryForUserProfile
    >,
    productsList?: Record<string, ProductsListItem>
): DataForEditGroups => {
    const { client } = result.data;
    const {
        clientConstants: {
            allowedBidModifierTypesByAdGroupTypes,
            allowedBidModifiersValues,
            clientLimits
        },
        adGroups: groupsResult,
        showConditions,
        feeds: originFeedsData,
        retargetings,
        smartFilters
    } = client;

    const { rowset: groupRows } = groupsResult;

    const showConditionsData = getShowConditionsData(showConditions.rowset);
    const campaignsData = getGroupsFilterCampaignInfo(groupRows);
    const smartFiltersData = getSmartFiltersData(
        smartFilters.rowset,
        campaignsData
    );
    const { feedsIds, feeds } = mapFeeds(originFeedsData.rowset);
    const retargetingData = getRetargetingData(retargetings.rowset);
    const userProfileRetargetingData = getUserProfileRetargetingData(
        userProfileRetargetings
    );

    const allowedRateCorrectionsData = getAllowedRateCorrectionsDataForGroup(
        allowedBidModifierTypesByAdGroupTypes
    );

    const rateCorrectionsConstants = mapRatesCorrectionsConstantsFromApiFormat(
        allowedBidModifiersValues,
        isDeviceModifiersAllowed
    );

    const resultResponse = {
        reqId: result.data.reqId,
        groups: groupRows.reduce(
            (
                acc: Record<string, EditGroupData>,
                groupRow: adGroupsRowFragmentForEdit
            ): Record<string, EditGroupData> => {
                const groupId = groupRow.id;
                const groupShowConditionsData =
                    showConditionsData[groupId] || getShowConditionsEmptyData();
                const {
                    retargetings: groupRetargetingConditions = [],
                    priceContext = undefined
                } = retargetingData[groupId] || {};

                let groupValue: EditGroupDataValue;

                switch (groupRow.__typename) {
                    case 'GdTextAdGroup':
                        groupValue = mapTextGroupData(
                            groupRow,
                            groupShowConditionsData,
                            groupRetargetingConditions,
                            regionTree,
                            allowedRateCorrectionsData[groupRow.type]
                        );
                        break;
                    case 'GdSmartAdGroup':
                        groupValue = mapSmartGroupData(
                            groupRow,
                            smartFiltersData[groupId],
                            regionTree,
                            allowedRateCorrectionsData[groupRow.type]
                        );
                        break;
                    case 'GdInternalAdGroup':
                        groupValue = mapInternalGroupData(
                            groupRow,
                            regionTree,
                            groupRow.campaign.type
                        );
                        break;
                    case 'GdCpmVideoAdGroup':
                    case 'GdCpmBannerAdGroup':
                    case 'GdCpmAudioAdGroup':
                    case 'GdCpmGeoproductAdGroup':
                    case 'GdCpmIndoorAdGroup':
                    case 'GdCpmOutdoorAdGroup':
                        groupValue = mapCpmGroupData(
                            groupRow,
                            groupShowConditionsData,
                            regionTree,
                            allowedRateCorrectionsData[groupRow.type],
                            priceContext,
                            userProfileRetargetingData[groupId],
                            productsList
                        );
                        break;
                    default:
                        groupValue = mapBaseGroupData(groupRow);
                        break;
                }

                acc[groupId] = {
                    id: String(groupId),
                    serverValue: { ...groupValue },
                    value: { ...groupValue },
                    keywordsEditor: {
                        value: groupShowConditionsData.keywords,
                        isOpen: false,
                        input: GroupsHelpers.getInputStringFromPhrasesList(
                            groupShowConditionsData.keywords
                        )
                    },
                    campaign: {
                        id: String(groupRow.campaign.id),
                        name: groupRow.campaign.name,
                        strategyType: groupRow.campaign.flatStrategy.type,
                        platform: groupRow.campaign.flatStrategy.platform,
                        strategy: {
                            isAutoBudget:
                                groupRow.campaign.flatStrategy.isAutoBudget
                        }
                    },
                    filtersEditor: {
                        isOpen: false,
                        isLoading: false,
                        auxiliaryFilter: ''
                    },
                    hasGeneralRate: groupRow.access.showGeneralPriceOnEdit,
                    isEditable: groupRow.access.canEdit,
                    isSaved: false,
                    errors: {},
                    bannersCount: groupRow.adsCount,
                    isNew: false,
                    showConditionsRecommendationData: {
                        wasClosed: false
                    }
                };

                return acc;
            },
            {}
        ),
        feedsIds,
        feeds,
        isLibraryListsFetched: false,
        allowedRateCorrectionsData,
        rateCorrectionsConstants,
        keywordsCountLimit: Number(clientLimits.keywordsCountLimit)
    };

    return resultResponse;
};

export const mapRetargetingConditionsQueryResultToQueryResponse = (
    login: string,
    result: ApolloQueryResult<RetargetingConditionsQueryForGroupEdit>
): UpdateRetargetingConditionsForGroupsEdit => {
    const { client } = result.data;
    const { retargetingConditions } = client;

    return {
        retargetingConditions: retargetingConditions.rowset.map(
            retargetingCondition =>
                mapRetargetingConditionFromApiFormat(
                    retargetingCondition,
                    login
                )
        )
    };
};

export const mapFeedCategory = (
    category: GroupsQueryForEdit_client_feeds_rowset_categories
): FeedCategory => {
    const {
        categoryId,
        parentCategoryId,
        isDeleted,
        name,
        offerCount
    } = category;

    return {
        categoryId,
        parentCategoryId,
        isDeleted,
        name,
        offerCount
    };
};

export const mapFeeds = (
    originFeeds: GroupsQueryForEdit_client_feeds_rowset[]
): {
    feedsIds: string[];
    feeds: Record<string, Feed>;
} => {
    const feedsIds: string[] = [];
    const feeds: Feeds = Object.create(null);

    originFeeds.forEach(feed => {
        const feedId = String(feed.id);

        feedsIds.push(feedId);
        feeds[feedId] = {
            id: feedId,
            name: feed.name,
            updateStatus: feed.updateStatus,
            businessType: feed.businessType,
            feedType: getValueFromApiFormat(feed.feedType),
            categories: feed.categories.map(mapFeedCategory)
        };
    });

    return {
        feedsIds,
        feeds
    };
};

export const makeGroupsForEditNewGroupData = (
    campaignData: CampaignBasicDataForGroupEdit_client_campaigns_rowset,
    regionTree: Region[],
    retargetingConditions: RetargetingConditionData[],
    allowedRateCorrectionsData: Record<GroupType, Array<GdBidModifierType>>,
    rateCorrectionsConstants: RatesCorrectionsConstantsData,
    keywordsCountLimit: string
): DataForEditGroups => {
    let groupInnerValue: EditGroupDataValue;

    switch (CampaignType[campaignData.type]) {
        case CampaignType.PERFORMANCE:
            groupInnerValue = makePerformanceGroupEmptyData(
                regionTree,
                allowedRateCorrectionsData,
                mapDefaultRegionIds(campaignData.defaultRegionIds)
            );
            break;
        case CampaignType.INTERNAL_FREE:
        case CampaignType.INTERNAL_DISTRIB:
            const type = mapCampaignRowTypeToCampaignType(campaignData.type);
            groupInnerValue = makeInternalGroupEmptyData(type);
            break;
        default:
            throw new Error(
                `создание групп типа ${campaignData.type} не поддерживается!`
            );
    }

    const dataForEditGroups: DataForEditGroups = makeNewDataForEditGroups(
        groupInnerValue,
        campaignData,
        keywordsCountLimit,
        allowedRateCorrectionsData,
        rateCorrectionsConstants,
        true
    );

    if (CampaignType[campaignData.type] === CampaignType.PERFORMANCE) {
        dataForEditGroups.retargetingConditions = retargetingConditions;
        dataForEditGroups.allowedRateCorrectionsData = allowedRateCorrectionsData;
        dataForEditGroups.rateCorrectionsConstants = rateCorrectionsConstants;
    }

    return dataForEditGroups;
};

export const makeGroupsForEditNewCpmGroupData = (
    campaignData: CampaignBasicDataForGroupEdit_client_campaigns_rowset,
    regionTree: Region[],
    allowedRateCorrectionsData: Record<GroupType, Array<GdBidModifierType>>,
    keywordsCountLimit: string,
    productsList: ProductsList,
    rateCorrectionsConstants: RatesCorrectionsConstantsData
): DataForEditGroups => {
    const groupInnerValue: EditGroupDataValue = makeCpmGroupEmptyData(
        regionTree,
        mapDefaultRegionIds(campaignData.defaultRegionIds),
        productsList
    );

    return makeNewDataForEditGroups(
        groupInnerValue,
        campaignData,
        keywordsCountLimit,
        allowedRateCorrectionsData,
        rateCorrectionsConstants,
        true
    );
};

export const makeNewDataForEditGroups = (
    groupInnerValue: EditGroupDataValue,
    campaignData: CampaignBasicDataForGroupEdit_client_campaigns_rowset,
    keywordsCountLimit: string,
    allowedRateCorrectionsData: Record<GroupType, Array<GdBidModifierType>>,
    rateCorrectionsConstants: RatesCorrectionsConstantsData,
    hasGeneralRate?: boolean
): DataForEditGroups => {
    const groupId = `${campaignData.id}${new Date().getTime()}`;

    return {
        groups: {
            [groupId]: {
                id: groupId,
                serverValue: { ...groupInnerValue },
                value: { ...groupInnerValue },
                keywordsEditor: {
                    value: [],
                    input: '',
                    isOpen: false
                },
                hasGeneralRate: Boolean(hasGeneralRate),
                isSaved: false,
                isEditable: true,
                errors: {},
                bannersCount: 0,
                campaign: {
                    id: campaignData.id,
                    name: campaignData.name,
                    strategyType: campaignData.flatStrategy.type,
                    strategy: {
                        isAutoBudget: campaignData.flatStrategy.isAutoBudget
                    }
                },
                filtersEditor: {
                    isOpen: false,
                    isLoading: false,
                    auxiliaryFilter: ''
                },
                isNew: true,
                showConditionsRecommendationData: {
                    wasClosed: false
                }
            }
        },
        feedsIds: [],
        feeds: {},
        isLibraryListsFetched: false,
        rateCorrectionsConstants,
        allowedRateCorrectionsData,
        keywordsCountLimit: Number(keywordsCountLimit)
    };
};

const mapDefaultRegionIds = (
    defaultRegionIds: (string | null)[]
): RegionId[] => {
    return defaultRegionIds
        .filter(regionId => regionId !== null)
        .map(regionId => Number(regionId));
};

const mapGroupToTextInput = (
    group: EditGroupData
): GdUpdateTextAdGroupItemInput => {
    const { value } = group;

    switch (value.campaignType) {
        case CampaignType.TEXT:
            const groupAutotargeting = value.autotargeting;
            const groupId = group.id;
            const campaignId = group.campaign.id;

            return {
                adGroupId: group.id,
                regionIds: value.regions.regionIds,
                adGroupName: value.name,
                adGroupMinusKeywords: value.minusKeywords,
                keywords: value.keywords.map(({ id, phrase, minusWords }) => ({
                    id: id,
                    phrase: mapPhraseWithMinusWordsToString(phrase, minusWords)
                })),
                relevanceMatch: groupAutotargeting.isActive
                    ? {
                          isActive: true,
                          id: groupAutotargeting.id || null
                      }
                    : {
                          isActive: false
                      },
                retargetings: value.retargeting.map(retargetingValue => {
                    return {
                        retCondId: retargetingValue.id,
                        id: retargetingValue.retargetingId || null
                    };
                }),
                useBidModifiers: true,
                bidModifiers: mapRatesCorrectionsValueToApiFormatForGroup(
                    value.rateCorrections,
                    groupId,
                    campaignId,
                    false
                ),
                generalPrice: value.generalRate
                    ? value.generalRate.toString()
                    : null,
                libraryMinusKeywordsIds: (value.selectedLibraryList || []).map(
                    list => list.id
                )
            };
        default:
            throw new Error('unsupported group type');
    }
};

const mapGroupToCpmInput = (
    group: EditGroupData,
    productsList: ProductsList,
    cryptaSegmentsByType: CryptaSegmentsByType
): GdUpdateCpmAdGroupItemInput => {
    switch (group.value.campaignType) {
        case CampaignType.CPM_BANNER:
            const {
                value,
                id: groupId,
                campaign: { id: campaignId },
                isNew
            } = group;
            const { showConditionType, audienceCollection = [] } = value;
            const selectedRestriction = productsList[
                CampaignType.CPM_BANNER
            ].restrictions.find(restriction => {
                return restriction.id === value.productRestrictionId;
            });
            const groupType = selectedRestriction
                ? selectedRestriction.groupType
                : GroupType.CPM_BANNER;

            const hasShowConditionType = ![
                GroupType.CPM_INDOOR,
                GroupType.CPM_OUTDOOR
            ].includes(groupType);
            const isUserProfile =
                showConditionType === CpmGroupShowConditionType.USER_PROFILE;
            const isKeywords =
                showConditionType === CpmGroupShowConditionType.KEYWORD;

            const groupMap = {
                [GroupType.CPM_INDOOR]: value.indoorMap,
                [GroupType.CPM_OUTDOOR]: value.outdoorMap
            }[groupType];

            const cpmGroupInput: GdUpdateCpmAdGroupItemInput = {
                type: GdCpmGroupTypeInput[groupType],
                campaignId: isNew ? campaignId : null,
                adGroupId: isNew ? null : groupId,
                regionIds: hasShowConditionType ? value.regions.regionIds : [],
                adGroupName: value.name,
                videoGoals: audienceCollection.map(
                    makeAudienceCollectionForApi
                ),
                bidModifiers: mapRatesCorrectionsValueToApiFormatForGroup(
                    value.rateCorrections,
                    groupId,
                    campaignId,
                    isNew
                ),
                keywords:
                    hasShowConditionType && isKeywords
                        ? (value.keywords || []).map(
                              ({ id, phrase, minusWords }) => ({
                                  id: id,
                                  phrase: mapPhraseWithMinusWordsToString(
                                      phrase,
                                      minusWords
                                  )
                              })
                          )
                        : null,
                retargetingCondition:
                    hasShowConditionType && isUserProfile
                        ? mapAudienceTargetingsToRetargetingConditionsApiFormat(
                              {
                                  audienceTargetings: value.audienceTargetings,
                                  audienceTargetingsTemplateName:
                                      value.audienceTargetingsTemplateName,
                                  cryptaSegmentsByType
                              }
                          )
                        : null,
                generalPrice: value.generalRate
                    ? value.generalRate.toString()
                    : null,
                pageBlocks: groupMap ? groupMap.pageBlocks : null
            };
            const availibleGroupType: Set<GroupType> = new Set([
                GroupType.CPM_VIDEO,
                GroupType.CPM_AUDIO
            ]);

            if (availibleGroupType.has(groupType)) {
                cpmGroupInput.videoGoals = audienceCollection.map(
                    makeAudienceCollectionForApi
                );
            }

            return cpmGroupInput;

        default:
            throw new Error('unsupported group type');
    }
};

const mapGroupToSmartInput = (
    group: EditGroupData
): GdUpdatePerformanceAdGroupItemInput => {
    const { value } = group;

    switch (value.campaignType) {
        case CampaignType.PERFORMANCE:
            const groupId = group.id;
            const campaignId = group.campaign.id;
            return {
                id: group.id,
                regionIds: value.regions.regionIds,
                trackingParams: value.hrefParams ? value.hrefParams : null,
                fieldToUseAsBody: value.bannerTextFeedField || null,
                fieldToUseAsName: value.bannerHeadersFeedField || null,
                minusKeywords: value.minusKeywords,
                libraryMinusKeywordsIds: (value.selectedLibraryList || []).map(
                    list => list.id
                ),
                name: value.name,
                bidModifiers: mapRatesCorrectionsValueToApiFormatForGroup(
                    value.rateCorrections,
                    groupId,
                    campaignId,
                    false
                )
            };
        default:
            throw new Error('unsupported group type');
    }
};

const mapTargetingShowDatesToApiFormat = (
    showDates?: TargetingShowDates
): ApiFormatTargetingShowDates | undefined => {
    if (!showDates) {
        return undefined;
    }

    const joinType = mapJoinTypeToApiFormat(showDates.joinType);
    const targetingMode = mapTargetingModeToApiFormat(showDates.targetingMode);
    const value = showDates.value.reduce((acc: string[], val: string) => {
        const date = val.trim();
        if (!date || date === 'Invalid date') {
            return acc;
        }

        return [...acc, date];
    }, []);

    if (value.length === 0) {
        return undefined;
    }

    return {
        joinType,
        targetingMode,
        value
    };
};

export const mapGroupToInternalInput = (
    group: EditGroupData
): GdUpdateInternalAdGroupsItemInput => {
    const { value } = group;
    switch (value.campaignType) {
        case CampaignType.INTERNAL_DISTRIB:
        case CampaignType.INTERNAL_FREE:
            return {
                id: group.id,
                regionIds: value.regions.regionIds,
                name: value.name,
                level: value.level,
                targetingInternalNetwork: value.targetingInternalNetwork
                    ? {
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingInternalNetwork.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingInternalNetwork.targetingMode
                          )
                      }
                    : undefined,
                targetingIsBrowser: value.targetingIsBrowser
                    ? {
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingIsBrowser.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingIsBrowser.targetingMode
                          )
                      }
                    : undefined,
                targetingIsMobile: value.targetingIsMobile
                    ? {
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingIsMobile.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingIsMobile.targetingMode
                          )
                      }
                    : undefined,
                targetingIsTouch: value.targetingIsTouch
                    ? {
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingIsTouch.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingIsTouch.targetingMode
                          )
                      }
                    : undefined,
                targetingIsTablet: value.targetingIsTablet
                    ? {
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingIsTablet.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingIsTablet.targetingMode
                          )
                      }
                    : undefined,
                targetingYandexUids: value.targetingYandexUids
                    ? {
                          value: value.targetingYandexUids.value
                              .split(',')
                              .map(uid => uid.trim()),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingYandexUids.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingYandexUids.targetingMode
                          )
                      }
                    : undefined,
                targetingOsNames: value.targetingOsNames
                    ? {
                          value: value.targetingOsNames.value.map(it => ({
                              targetingValueEntryId: it.targetingValueEntryId
                          })),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingOsNames.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingOsNames.targetingMode
                          )
                      }
                    : undefined,
                targetingOsFamilies: value.targetingOsFamilies
                    ? {
                          value: value.targetingOsFamilies.value.map(it => ({
                              targetingValueEntryId: it.targetingValueEntryId
                          })),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingOsFamilies.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingOsFamilies.targetingMode
                          )
                      }
                    : undefined,
                targetingBrowserNames: value.targetingBrowserNames
                    ? {
                          value: value.targetingBrowserNames.value.map(it => ({
                              targetingValueEntryId: it.targetingValueEntryId
                          })),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingBrowserNames.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingBrowserNames.targetingMode
                          )
                      }
                    : undefined,
                targetingBrowserEngines: value.targetingBrowserEngines
                    ? {
                          value: value.targetingBrowserEngines.value.map(
                              it => ({
                                  targetingValueEntryId:
                                      it.targetingValueEntryId
                              })
                          ),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingBrowserEngines.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingBrowserEngines.targetingMode
                          )
                      }
                    : undefined,
                targetingDeviceVendors: value.targetingDeviceVendors
                    ? {
                          value: value.targetingDeviceVendors.value.map(it => ({
                              targetingValueEntryId: it.targetingValueEntryId
                          })),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingDeviceVendors.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingDeviceVendors.targetingMode
                          )
                      }
                    : undefined,
                targetingInterfaceLangs: value.targetingInterfaceLangs
                    ? {
                          // FIXME: костыль
                          value: (value.targetingInterfaceLangs
                              .value as unknown) as InterfaceLangInput[],
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingInterfaceLangs.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingInterfaceLangs.targetingMode
                          )
                      }
                    : undefined,
                targetingDeviceNames: value.targetingDeviceNames
                    ? {
                          value: value.targetingDeviceNames.value
                              .split(',')
                              .map(it => it.trim()),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingDeviceNames.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingDeviceNames.targetingMode
                          )
                      }
                    : undefined,
                targetingQueryReferers: value.targetingQueryReferers
                    ? {
                          value: value.targetingQueryReferers.value
                              .split(',')
                              .map(it => it.trim()),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingQueryReferers.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingQueryReferers.targetingMode
                          )
                      }
                    : undefined,
                targetingUserAgents: value.targetingUserAgents
                    ? {
                          value: value.targetingUserAgents.value
                              .split(',')
                              .map(it => it.trim()),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingUserAgents.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingUserAgents.targetingMode
                          )
                      }
                    : undefined,
                targetingShowDates: mapTargetingShowDatesToApiFormat(
                    value.targetingShowDates
                ),
                startTime: value.startTime,
                finishTime: value.finishTime
            };
        default:
            throw new Error('unsupported group type');
    }
};

export const mapNewGroupToInternalInput = (
    group: EditGroupData
): GdAddInternalAdGroupsItemInput => {
    const {
        value,
        campaign: { id }
    } = group;
    switch (value.campaignType) {
        case CampaignType.INTERNAL_DISTRIB:
        case CampaignType.INTERNAL_FREE:
            return {
                campaignId: id,
                regionIds: value.regions.regionIds,
                name: value.name,
                level: value.level,
                targetingInternalNetwork: value.targetingInternalNetwork
                    ? {
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingInternalNetwork.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingInternalNetwork.targetingMode
                          )
                      }
                    : undefined,
                targetingIsBrowser: value.targetingIsBrowser
                    ? {
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingIsBrowser.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingIsBrowser.targetingMode
                          )
                      }
                    : undefined,
                targetingIsMobile: value.targetingIsMobile
                    ? {
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingIsMobile.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingIsMobile.targetingMode
                          )
                      }
                    : undefined,
                targetingIsTouch: value.targetingIsTouch
                    ? {
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingIsTouch.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingIsTouch.targetingMode
                          )
                      }
                    : undefined,
                targetingIsTablet: value.targetingIsTablet
                    ? {
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingIsTablet.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingIsTablet.targetingMode
                          )
                      }
                    : undefined,
                targetingYandexUids: value.targetingYandexUids
                    ? {
                          value: value.targetingYandexUids.value
                              .split(',')
                              .map(uid => uid.trim()),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingYandexUids.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingYandexUids.targetingMode
                          )
                      }
                    : undefined,
                targetingOsNames: value.targetingOsNames
                    ? {
                          value: value.targetingOsNames.value.map(it => ({
                              targetingValueEntryId: it.targetingValueEntryId
                          })),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingOsNames.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingOsNames.targetingMode
                          )
                      }
                    : undefined,
                targetingOsFamilies: value.targetingOsFamilies
                    ? {
                          value: value.targetingOsFamilies.value.map(it => ({
                              targetingValueEntryId: it.targetingValueEntryId
                          })),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingOsFamilies.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingOsFamilies.targetingMode
                          )
                      }
                    : undefined,
                targetingBrowserNames: value.targetingBrowserNames
                    ? {
                          value: value.targetingBrowserNames.value.map(it => ({
                              targetingValueEntryId: it.targetingValueEntryId
                          })),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingBrowserNames.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingBrowserNames.targetingMode
                          )
                      }
                    : undefined,
                targetingBrowserEngines: value.targetingBrowserEngines
                    ? {
                          value: value.targetingBrowserEngines.value.map(
                              it => ({
                                  targetingValueEntryId:
                                      it.targetingValueEntryId
                              })
                          ),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingBrowserEngines.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingBrowserEngines.targetingMode
                          )
                      }
                    : undefined,
                targetingDeviceVendors: value.targetingDeviceVendors
                    ? {
                          value: value.targetingDeviceVendors.value.map(it => ({
                              targetingValueEntryId: it.targetingValueEntryId
                          })),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingDeviceVendors.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingDeviceVendors.targetingMode
                          )
                      }
                    : undefined,
                targetingInterfaceLangs: value.targetingInterfaceLangs
                    ? {
                          // FIXME: костыль
                          value: (value.targetingInterfaceLangs
                              .value as unknown) as InterfaceLangInput[],
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingInterfaceLangs.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingInterfaceLangs.targetingMode
                          )
                      }
                    : undefined,
                targetingDeviceNames: value.targetingDeviceNames
                    ? {
                          value: value.targetingDeviceNames.value
                              .split(',')
                              .map(it => it.trim()),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingDeviceNames.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingDeviceNames.targetingMode
                          )
                      }
                    : undefined,
                targetingQueryReferers: value.targetingQueryReferers
                    ? {
                          value: value.targetingQueryReferers.value
                              .split(',')
                              .map(it => it.trim()),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingQueryReferers.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingQueryReferers.targetingMode
                          )
                      }
                    : undefined,
                targetingUserAgents: value.targetingUserAgents
                    ? {
                          value: value.targetingUserAgents.value
                              .split(',')
                              .map(it => it.trim()),
                          joinType: mapJoinTypeToApiFormat(
                              value.targetingUserAgents.joinType
                          ),
                          targetingMode: mapTargetingModeToApiFormat(
                              value.targetingUserAgents.targetingMode
                          )
                      }
                    : undefined,
                targetingShowDates: mapTargetingShowDatesToApiFormat(
                    value.targetingShowDates
                ),
                startTime: value.startTime,
                finishTime: value.finishTime
            };
        default:
            throw new Error('unsupported group type');
    }
};

type MappedData = [
    GdUpdateTextAdGroupInput,
    GdUpdatePerformanceAdGroupInput,
    Array<GroupId>,
    Array<GroupId>
];

type MappedCpmData = [GdUpdateCpmAdGroupInput, Array<GroupId>];

export const mapGroupsDataToApiFormat = (
    groupsList: Record<GroupId, EditGroupData>
): MappedData => {
    const allGroups: Array<EditGroupData> = Object.keys(groupsList).map(
        (groupId: GroupId) => groupsList[groupId]
    );
    const textGroups: Array<EditGroupData> = allGroups.filter(
        ({ value }: EditGroupData) => value.campaignType === CampaignType.TEXT
    );
    const smartGroups: Array<EditGroupData> = allGroups.filter(
        ({ value }: EditGroupData) =>
            value.campaignType === CampaignType.PERFORMANCE
    );

    return [
        {
            updateItems: textGroups.map(mapGroupToTextInput)
        },
        {
            updateItems: smartGroups.map(mapGroupToSmartInput)
        },
        textGroups.map(({ id }) => id),
        smartGroups.map(({ id }) => id)
    ];
};

export const mapCpmGroupsDataToApiFormat = (
    groupsList: Record<GroupId, EditGroupData>,
    productsList: ProductsList,
    cryptaSegmentsByType: CryptaSegmentsByType
): MappedCpmData => {
    const allGroups: Array<EditGroupData> = Object.keys(groupsList).map(
        (groupId: GroupId) => groupsList[groupId]
    );
    const cpmGroups: Array<EditGroupData> = allGroups.filter(
        ({ value }: EditGroupData) =>
            value.campaignType === CampaignType.CPM_BANNER
    );

    return [
        {
            isNewGroups: cpmGroups.some(({ isNew }) => isNew),
            updateCpmAdGroupItems: cpmGroups.map(cpmGroup =>
                mapGroupToCpmInput(cpmGroup, productsList, cryptaSegmentsByType)
            )
        },
        cpmGroups.map(({ id }) => id)
    ];
};

export const mapEditorRegionsToRegionsData = (
    groupId: GroupId,
    regions: EditGroupRegions,
    regionsEditor: Record<GroupId, RegionsEditorState>,
    regionTree: Region[]
): EditGroupRegions => {
    const currentRegionsEditor =
        regionsEditor[groupId] || defaultRegionsEditorData;

    return {
        regionIds: currentRegionsEditor.isOpen
            ? RegionsHelpers.convertRegionsSelectedInfoToFlattenList(
                  RegionsHelpers.getSelectedRegionsInfo(
                      regionTree,
                      currentRegionsEditor.selectedMap
                  )
              )
            : regions.regionIds,
        selectedMap: currentRegionsEditor.isOpen
            ? currentRegionsEditor.selectedMap
            : regions.selectedMap
    };
};

const getGroupMinusKeywords = (
    group: EditGroupData,
    minusPhraseEditor: MinusPhraseLibraryEditorState
): string[] => {
    switch (group.value.campaignType) {
        case CampaignType.TEXT:
        case CampaignType.PERFORMANCE:
            if (minusPhraseEditor.data === undefined) {
                return group.value.minusKeywords;
            }

            let minusPhrasesFromMassActions: string[] = [];
            // Если были применены настройки из массовых действий, берем значения из редактора
            if (minusPhraseEditor.data[MassActionId.groupsEdit] !== undefined) {
                minusPhrasesFromMassActions =
                    minusPhraseEditor.data[MassActionId.groupsEdit]
                        .minusKeywordsEditor.value;
            }

            // Если что-то менялось в редакторе, берем значения из открытого редактора
            // Или сохраненные значения. При этом изменения из массовых действий учитываются
            const minusPhrasesFromEditor =
                minusPhraseEditor.data[group.id] &&
                minusPhraseEditor.data[group.id].minusKeywordsEditor.isOpen
                    ? minusPhraseEditor.data[group.id].minusKeywordsEditor.value
                    : group.value.minusKeywords;

            return minusPhrasesFromEditor && minusPhrasesFromEditor.length !== 0
                ? minusPhrasesFromEditor
                : minusPhrasesFromMassActions;
        default:
            return [];
    }
};

const getGroupAudienceTargetings = (
    groupId: GroupId,
    audienceTargetings: AudienceTargetingsValue,
    interestsEditors: InterestsEditorsState,
    socialDemoEditors: CryptaSocialDemoEditorsState,
    groupFamilyEditors: CryptaGroupFamilyEditorsState,
    audioGenresEditors: CryptaAudioGenresEditorsState,
    behavioralEditors: CryptaBehavioralEditorsState
): AudienceTargetingsValue => {
    const interestsEditor = interestsEditors[groupId] || {};
    const socialDemoEditor =
        socialDemoEditors[groupId] || cryptaSocialDemoEditorsInitialState;
    const groupFamilyEditor =
        groupFamilyEditors[groupId] || cryptaGroupFamilyEditorsInitialState;
    const audioGenresEditor =
        audioGenresEditors[groupId] || cryptaAudioGenresEditorsInitialState;
    const behavioralEditor =
        behavioralEditors[groupId] || cryptaBehavioralEditorsInitialState;

    return {
        socialDemo: socialDemoEditor.isOpen
            ? socialDemoEditor.checkedItems
            : audienceTargetings[AudienceTargetingsType.socialDemo],
        family: groupFamilyEditor.isOpen
            ? groupFamilyEditor.checkedItems
            : audienceTargetings[AudienceTargetingsType.family],
        audioGenres: audioGenresEditor.isOpen
            ? audioGenresEditor.checkedItems
            : audienceTargetings[AudienceTargetingsType.audioGenres],
        behaviors: behavioralEditor.isOpen
            ? behavioralEditor.checkedItems
            : audienceTargetings[AudienceTargetingsType.behaviors],
        interests: interestsEditor.isOpen
            ? interestsEditor.value
            : audienceTargetings[AudienceTargetingsType.interests],
        audienceMetrics:
            audienceTargetings[AudienceTargetingsType.audienceMetrics]
    };
};

const getGroupSelectedLibraryList = (
    group: EditGroupData,
    minusPhraseEditor: MinusPhraseLibraryEditorState
): MinusPhrasesShortLibraryItem[] | undefined => {
    switch (group.value.campaignType) {
        case CampaignType.TEXT:
        case CampaignType.PERFORMANCE:
            if (minusPhraseEditor.data === undefined) {
                return group.value.selectedLibraryList;
            }

            let selectedLibraryListFromMassActions: MinusPhrasesShortLibraryItem[] = [];
            // Если были применены настройки из массовых действий, берем значения из редактора
            if (minusPhraseEditor.data[MassActionId.groupsEdit] !== undefined) {
                selectedLibraryListFromMassActions = (
                    minusPhraseEditor.data[MassActionId.groupsEdit]
                        .minusKeywordsEditor.selectedLibraryListItems || []
                ).map(list => {
                    return { id: list, name: '' };
                });
            }

            // Если что-то менялось в редакторе, берем значения из открытого редактора
            // Или сохраненные значения. При этом изменения из массовых действий учитываются
            const selectedLibraryListFromEditor =
                minusPhraseEditor.data[group.id] &&
                minusPhraseEditor.data[group.id].minusKeywordsEditor.isOpen
                    ? (
                          minusPhraseEditor.data[group.id].minusKeywordsEditor
                              .selectedLibraryListItems || []
                      ).map(list => {
                          return { id: list, name: '' };
                      })
                    : group.value.selectedLibraryList;

            return selectedLibraryListFromEditor &&
                selectedLibraryListFromEditor.length !== 0
                ? selectedLibraryListFromEditor
                : selectedLibraryListFromMassActions;
        default:
            return [];
    }
};

const getGroupRateCorrections = (
    editorData: Record<GroupId, RateCorrectionEditorState>,
    groupId: GroupId,
    rateCorrections: Record<RateCorrectionType, RateCorrection>
): RatesCorrectionsValue =>
    editorData[groupId] && editorData[groupId].isVisible
        ? mapEditorRateCorrectionsToRateCorrections(editorData[groupId].value)
        : rateCorrections;

export const getGroupsValueFromOpenedEditors = (
    groupsData: Record<GroupId, EditGroupData>,
    rateCorrectionsData: Record<GroupId, RateCorrectionEditorState>,
    regionsEditor: Record<GroupId, RegionsEditorState>,
    retargetingEditor: Record<GroupId, RetargetingEditorState>,
    minusPhraseEditor: MinusPhraseLibraryEditorState,
    nameEditors: EditPageItemNameEditorsState,
    interestsEditor: InterestsEditorsState,
    socialDemoEditor: CryptaSocialDemoEditorsState,
    groupFamilyEditor: CryptaGroupFamilyEditorsState,
    audioGenresEditor: CryptaAudioGenresEditorsState,
    behavioralEditor: CryptaBehavioralEditorsState,
    regionTree: Region[],
    editableTypes: Set<GroupType>
): Record<GroupId, EditGroupData> => {
    return Object.keys(groupsData).reduce(
        (acc: Record<GroupId, EditGroupData>, key: GroupId) => {
            const group = groupsData[key];

            if (editableTypes.has(group.value.groupType)) {
                acc[key] = getValueForEditGroup(
                    group,
                    rateCorrectionsData,
                    regionsEditor,
                    retargetingEditor,
                    minusPhraseEditor,
                    nameEditors,
                    interestsEditor,
                    socialDemoEditor,
                    groupFamilyEditor,
                    audioGenresEditor,
                    behavioralEditor,
                    regionTree
                );
            }

            return acc;
        },
        {}
    );
};

function getValueForEditGroup(
    group: EditGroupData,
    rateCorrectionsData: Record<GroupId, RateCorrectionEditorState>,
    regionsEditor: Record<GroupId, RegionsEditorState>,
    retargetingEditor: Record<GroupId, RetargetingEditorState>,
    minusPhraseEditor: MinusPhraseLibraryEditorState,
    nameEditors: EditPageItemNameEditorsState,
    interestsEditor: InterestsEditorsState,
    socialDemoEditor: CryptaSocialDemoEditorsState,
    groupFamilyEditor: CryptaGroupFamilyEditorsState,
    audioGenresEditor: CryptaAudioGenresEditorsState,
    behavioralEditor: CryptaBehavioralEditorsState,
    regionTree: Region[]
): EditGroupData {
    const groupNameEditor = selectEditPageItemNameEditorValue(
        nameEditors,
        GroupsHelpers.getGroupEditPageItemEditorId(group.id)
    );

    switch (group.value.campaignType) {
        case CampaignType.TEXT:
            return {
                ...group,
                value: {
                    ...group.value,
                    name: groupNameEditor.isVisible
                        ? groupNameEditor.value
                        : group.value.name,
                    keywords: group.keywordsEditor.isOpen
                        ? group.keywordsEditor.value
                        : group.value.keywords,
                    regions: mapEditorRegionsToRegionsData(
                        group.id,
                        group.value.regions,
                        regionsEditor,
                        regionTree
                    ),
                    retargeting:
                        retargetingEditor[group.id] &&
                        retargetingEditor[group.id].isVisible
                            ? mapRetargetingEditorValueToGroupRetargetingValue(
                                  retargetingEditor[group.id].conditions,
                                  group.value.retargeting
                              )
                            : group.value.retargeting,
                    rateCorrections: getGroupRateCorrections(
                        rateCorrectionsData,
                        group.id,
                        group.value.rateCorrections
                    ),
                    minusKeywords: getGroupMinusKeywords(
                        group,
                        minusPhraseEditor
                    ),
                    selectedLibraryList: getGroupSelectedLibraryList(
                        group,
                        minusPhraseEditor
                    )
                }
            };

        case CampaignType.PERFORMANCE:
            return {
                ...group,
                value: {
                    ...group.value,
                    name: groupNameEditor.isVisible
                        ? groupNameEditor.value
                        : group.value.name,
                    regions: mapEditorRegionsToRegionsData(
                        group.id,
                        group.value.regions,
                        regionsEditor,
                        regionTree
                    ),
                    minusKeywords: getGroupMinusKeywords(
                        group,
                        minusPhraseEditor
                    ),
                    selectedLibraryList: getGroupSelectedLibraryList(
                        group,
                        minusPhraseEditor
                    ),
                    rateCorrections: getGroupRateCorrections(
                        rateCorrectionsData,
                        group.id,
                        group.value.rateCorrections
                    )
                }
            };

        case CampaignType.CPM_BANNER: {
            return {
                ...group,
                value: {
                    ...group.value,
                    name: groupNameEditor.isVisible
                        ? groupNameEditor.value
                        : group.value.name,
                    keywords: group.keywordsEditor.isOpen
                        ? group.keywordsEditor.value
                        : group.value.keywords,
                    regions: mapEditorRegionsToRegionsData(
                        group.id,
                        group.value.regions,
                        regionsEditor,
                        regionTree
                    ),
                    audienceTargetings: getGroupAudienceTargetings(
                        group.id,
                        group.value.audienceTargetings,
                        interestsEditor,
                        socialDemoEditor,
                        groupFamilyEditor,
                        audioGenresEditor,
                        behavioralEditor
                    ),
                    rateCorrections:
                        rateCorrectionsData[group.id] &&
                        rateCorrectionsData[group.id].isVisible
                            ? mapEditorRateCorrectionsToRateCorrections(
                                  rateCorrectionsData[group.id].value
                              )
                            : group.value.rateCorrections
                }
            };
        }

        default:
            return group;
    }
}

const getDateStringFromDateEditor = (
    date?: moment.Moment,
    time?: string
): GraphQlLocalDateTime | undefined => {
    if (!date || !time) {
        return undefined;
    }

    return `${date.format('YYYY-MM-DD')}T${time}`;
};

export const getInternalGroupsValueFromEditors = (
    groupsData: Record<GroupId, EditGroupData>,
    regionsEditor: Record<GroupId, RegionsEditorState>,
    nameEditors: EditPageItemNameEditorsState,
    dateEditors: DatesIntervalEditorsState,
    targetingEditors: TagCloudTargetingsState,
    regionTree: Region[]
): Record<GroupId, EditGroupData> => {
    return mapValues(groupsData, (group: EditGroupData) => {
        const groupNameEditor = selectEditPageItemNameEditorValue(
            nameEditors,
            GroupsHelpers.getGroupEditPageItemEditorId(group.id)
        );
        const dateEditor = dateEditors[group.id];
        const targetingShowDates =
            targetingEditors[
                getTagCloudTargetingId(
                    AdditionalTargetingKey.SHOW_DATES,
                    group.id
                )
            ];

        switch (group.value.campaignType) {
            case CampaignType.INTERNAL_DISTRIB:
            case CampaignType.INTERNAL_FREE:
                return {
                    ...group,
                    value: {
                        ...group.value,
                        name: groupNameEditor.isVisible
                            ? groupNameEditor.value
                            : group.value.name,
                        regions: mapEditorRegionsToRegionsData(
                            group.id,
                            group.value.regions,
                            regionsEditor,
                            regionTree
                        ),
                        startTime: getDateStringFromDateEditor(
                            dateEditor.startDate,
                            dateEditor.startTime
                        ),
                        finishTime: getDateStringFromDateEditor(
                            dateEditor.endDate,
                            dateEditor.endTime
                        ),
                        targetingShowDates: mapTagCloudTargetingValue(
                            targetingShowDates
                        )
                    }
                };
            default:
                return group;
        }
    });
};

export const mapDuplicateGroupsApiReponseData = (
    data: CopyAdGroupsInfoQuery
): Array<DuplicateItem> => {
    const {
        client: {
            adGroups: { rowset }
        }
    } = data;

    return rowset.map(item => {
        let isDisabled = false;

        // TODO Дублирование групп доступно пока только для ТГО, видео и баннеров.
        if (
            item.campaign.type !== GdCampaignType.TEXT &&
            item.__typename !== GdAdGroup.GdCpmBannerAdGroup &&
            item.__typename !== GdAdGroup.GdCpmVideoAdGroup
        ) {
            isDisabled = true;
        }

        return {
            campaignName: item.campaign.name,
            groupName: item.name,
            groupId: item.id,
            campaignId: item.campaign.id,
            campaignType: mapCampaignRowTypeToCampaignType(item.campaign.type),
            isInvalid: false,
            isDisabled
        };
    });
};

// этот метод нужен для предсказуемого упорядочивания кампаний и групп на странице редактирования
export const groupGroupsByCampaignId = (
    groupsHash: Record<GroupId, EditGroupData>
): Record<string, Array<EditGroupData>> => {
    return reduce(
        map(
            groupBy(groupsHash, group => group.campaign.id),
            (
                groupsArr: Array<EditGroupData>,
                campaignId: string
            ): Record<string, Array<EditGroupData>> => ({
                [campaignId]: sortBy(
                    groupsArr,
                    ({ id }: EditGroupData): number => Number(id)
                )
            })
        ),
        (acc, groupItems) => ({ ...acc, ...groupItems }),
        {}
    );
};

const mapFeedFilterConditionToApiFormat = (
    field: string,
    operator: GdSmartFilterConditionOperator,
    value: FeedFilterOperationControlValues | boolean | number
): GdAddSmartFilterConditionInput => {
    return {
        field,
        operator: GdSmartFilterConditionOperatorInput[operator],
        stringValue: JSON.stringify(value)
    };
};

const mapFeedFilterTreeTabConditionsToApiFormat = (
    filter: EditGroupFilterItem
): Array<GdAddSmartFilterConditionInput> => {
    const { priceRange, vendors, feedCategories } = filter;
    const conditions = [];

    const [minPrice, maxPrice] = priceRange;
    if (minPrice !== 0 || maxPrice !== 0) {
        const priceRangeCondition = mapFeedFilterConditionToApiFormat(
            'price',
            GdSmartFilterConditionOperator.RANGE,
            [priceRange.join('-')]
        );

        conditions.push(priceRangeCondition);
    }

    if (vendors) {
        const vendorsCondition = mapFeedFilterConditionToApiFormat(
            'vendor',
            GdSmartFilterConditionOperator.CONTAINS_ANY,
            vendors.split(',')
        );

        conditions.push(vendorsCondition);
    }

    const selectedCategories = FeedCategoriesHelpers.filterNodes(
        FeedCategoriesHelpers.isSelected,
        feedCategories
    );
    const flatCategories = FeedCategoriesHelpers.getFlattenedTree(
        selectedCategories
    ).map(category => category.id);

    const categoriesIdsCondition = mapFeedFilterConditionToApiFormat(
        'categoryId',
        GdSmartFilterConditionOperator.EQUALS_ANY,
        flatCategories
    );

    return conditions.concat(categoriesIdsCondition);
};

const mapFeedFilterConditionTabConditionsToApiFormat = (
    filter: EditGroupFilterItem
): Array<GdAddSmartFilterConditionInput> => {
    return filter.conditions.map(condition => {
        const { operator, field, arguments: values } = condition;

        if (operator === GdSmartFilterConditionOperator.EXISTS) {
            return mapFeedFilterConditionToApiFormat(field, operator, 1);
        }

        if (operator === GdSmartFilterConditionOperator.RANGE) {
            return mapFeedFilterConditionToApiFormat(
                field,
                operator,
                (values as Array<NumberRangeInputValues>).map(range =>
                    range.join('-')
                )
            );
        }

        return mapFeedFilterConditionToApiFormat(field, operator, values);
    });
};

const mapFeedFilterConditionsToApiFormat = (
    filter: EditGroupFilterItem,
    schema: FeedFilterSchema
): Array<GdAddSmartFilterConditionInput> => {
    const { tab, isAvailable } = filter;

    const isAvailableCondition = mapFeedFilterConditionToApiFormat(
        'available',
        GdSmartFilterConditionOperator.EQUALS_ANY,
        isAvailable
    );

    const hasAvailableInSchema = schemaHasAvailableCondition(schema);

    const conditions = hasAvailableInSchema ? [isAvailableCondition] : [];

    switch (tab) {
        case GdSmartFilterTab.ALL_PRODUCTS:
            return [];
        case GdSmartFilterTab.TREE:
            return [
                ...mapFeedFilterTreeTabConditionsToApiFormat(filter),
                ...conditions
            ];
        case GdSmartFilterTab.CONDITION:
            return [
                ...mapFeedFilterConditionTabConditionsToApiFormat(filter),
                ...conditions
            ];
        default:
            throw new Error('Таб не определен');
    }
};

export const mapFeedFilterDataToAddApiFormat = (
    filter: EditGroupFilterItem,
    groupId: GroupId,
    schema: FeedFilterSchema,
    campaignStrategy?: GdCampaignStrategyType
): GdAddSmartFiltersInput => {
    const budgetCalculation = defineSmartFilterBudgetCalculation(
        campaignStrategy
    );

    return {
        addItems: [
            {
                ...mapFeedFilterPricesToApiFormat(filter, budgetCalculation),
                tab: GdSmartFilterTabInput[filter.tab],
                adGroupId: groupId,
                name: filter.name,
                targetFunnel: GdSmartFilterTargetFunnelInput[filter.type],
                isSuspended: false,
                conditions: mapFeedFilterConditionsToApiFormat(filter, schema)
            }
        ]
    };
};

export const mapFeedFilterDataToUpdateApiFormat = (
    filter: EditGroupFilterItem,
    schema: FeedFilterSchema,
    campaignStrategy?: GdCampaignStrategyType
): GdUpdateSmartFiltersInput => {
    const budgetCalculation = defineSmartFilterBudgetCalculation(
        campaignStrategy
    );

    return {
        updateItems: [
            {
                ...mapFeedFilterPricesToApiFormat(filter, budgetCalculation),
                tab: GdSmartFilterTabInput[filter.tab],
                id: filter.id,
                name: filter.name,
                targetFunnel: GdSmartFilterTargetFunnelInput[filter.type],
                isSuspended: false,
                conditions: mapFeedFilterConditionsToApiFormat(filter, schema)
            }
        ]
    };
};

export const mapBidValueToApiFormat = (bidValue?: number): number => {
    const resetBidValue = 0;

    return isUndefined(bidValue) ? resetBidValue : bidValue;
};

const mapFeedFilterPricesToApiFormat = (
    filter: EditGroupFilterItem,
    budgetCalculation: SmartGroupBudgetCalculation
): {
    priceCpc?: string;
    priceCpa?: string;
    autobudgetPriority?: GdShowConditionAutobudgetPriorityInput;
} => {
    const { priceCpa, priceCpc, autobudgetPriority } = filter;

    const formattedPriceCpa = String(mapBidValueToApiFormat(priceCpa));
    const formattedPriceCpc = String(mapBidValueToApiFormat(priceCpc));

    switch (budgetCalculation) {
        case SmartGroupBudgetCalculation.FULL:
            return { priceCpc: formattedPriceCpc, priceCpa: formattedPriceCpa };
        case SmartGroupBudgetCalculation.CPC:
            return { priceCpc: formattedPriceCpc };
        case SmartGroupBudgetCalculation.AUTO:
        default:
            return {
                autobudgetPriority:
                    autobudgetPriority &&
                    GdShowConditionAutobudgetPriorityInput[autobudgetPriority]
            };
    }
};

const mapFeedFilterEnumParamsFromApiResponse = (
    values: FeedFilterEnumFieldParams['values'],
    fieldName: string
): FeedFilterEnumFieldParams['values'] => {
    if (fieldName === 'age') {
        return values.sort((a, b) => Number(a) - Number(b));
    }

    return values;
};

const mapFeedFilterOperatorFromApiResponse = (
    filter: SmartFeedSchemaQuery_constants_smartFilterSchemas_fields_GdSmartFilterStringField_operators
): FeedFilterSchemaOperator => {
    return {
        maxItems: filter.maxItems,
        type: filter.type
    };
};

export const mapFeedFilterSchemaFromApiResponse = (
    data: SmartFeedSchemaQuery
): FeedFilterSchema => {
    const [schema] = data.constants.smartFilterSchemas || [null];
    return {
        fields: !schema
            ? []
            : (schema.fields
                  .map(
                      (
                          field: SmartFeedSchemaQuery_constants_smartFilterSchemas_fields
                      ): FeedFilterSchemaField | undefined => {
                          switch (field.__typename) {
                              case 'GdSmartFilterNumberField':
                                  return {
                                      fieldType:
                                          FeedFilterSchemaFieldType.NUMBER,
                                      name: field.name,
                                      max: field.max,
                                      min: field.min,
                                      precision: field.precision,
                                      operators: field.operators.map(
                                          mapFeedFilterOperatorFromApiResponse
                                      )
                                  };
                              case 'GdSmartFilterStringField':
                                  return {
                                      fieldType:
                                          FeedFilterSchemaFieldType.STRING,
                                      name: field.name,
                                      operators: field.operators.map(
                                          mapFeedFilterOperatorFromApiResponse
                                      ),
                                      maxLength: field.maxLength,
                                      minLength: field.minLength,
                                      pattern: getValueFromApiFormat(
                                          field.pattern
                                      )
                                  };
                              case 'GdSmartFilterEnumField':
                                  return {
                                      fieldType: FeedFilterSchemaFieldType.ENUM,
                                      name: field.name,
                                      operators: field.operators.map(
                                          mapFeedFilterOperatorFromApiResponse
                                      ),
                                      values: mapFeedFilterEnumParamsFromApiResponse(
                                          field.values,
                                          field.name
                                      )
                                  };
                              default:
                                  return;
                          }
                      }
                  )
                  .filter(Boolean) as Array<FeedFilterSchemaField>)
    };
};

export const defineSmartFilterBudgetCalculation = (
    campaignStrategyType?: GdCampaignStrategyType
): SmartGroupBudgetCalculation => {
    switch (campaignStrategyType) {
        case GdCampaignStrategyType.AVG_CPA_PER_CAMP:
        case GdCampaignStrategyType.AVG_CPA_PER_FILTER:
            return SmartGroupBudgetCalculation.FULL;
        case GdCampaignStrategyType.AVG_CPC_PER_CAMP:
        case GdCampaignStrategyType.AVG_CPC_PER_FILTER:
            return SmartGroupBudgetCalculation.CPC;
        default:
            return SmartGroupBudgetCalculation.AUTO;
    }
};

export const isSmartFilterTreeSchema = (schema: FeedFilterSchema): boolean => {
    return schema.fields.some(field => field.name === 'categoryId');
};

export const schemaHasAvailableCondition = (
    schema: FeedFilterSchema
): boolean => {
    return schema.fields.some(field => field.name === 'available');
};

export const getFiltersSaveValidationResult = ({
    errors,
    data
}: FetchResult<AddSmartFilters | UpdateSmartFilters>): Array<
    ValidationResultItem
> => {
    const filterMutationOperations = ['addSmartFilters', 'updateFilters'];

    return filterMutationOperations.reduce((acc, operation) => {
        const currentErrors = get(
            data,
            `${operation}.validationResult.errors`,
            []
        ).map((error: ValidationError) => {
            return pick(error, ['code', 'params', 'path']);
        });
        return [...acc, ...currentErrors];
    }, []);
};

const getDefaultTraitsSelectItems = (): StringDictsItems => {
    return {
        [GdStringDictName.UA_TRAITS_BROWSER_ENGINE]: [],
        [GdStringDictName.UA_TRAITS_BROWSER_NAME]: [],
        [GdStringDictName.UA_TRAITS_DEVICE_VENDOR]: [],
        [GdStringDictName.UA_TRAITS_OS_FAMILY]: [],
        [GdStringDictName.UA_TRAITS_OS_NAME]: []
    };
};

export const mapUaTraitApiResponseToSelectItems = (
    response: ApolloQueryResult<UaTraitsQuery>
): StringDictsItems => {
    const defaultTraitsSelectItems = getDefaultTraitsSelectItems();

    if (!response.data.constants.stringDicts) {
        return defaultTraitsSelectItems;
    }

    return response.data.constants.stringDicts.reduce((acc, stringDict) => {
        if (!stringDict) {
            return {
                ...defaultTraitsSelectItems,
                ...acc
            };
        }

        return {
            ...acc,
            [stringDict.dictName]: stringDict.entries.map(entry => ({
                val: (entry as UaTraitsQuery_constants_stringDicts_entries_GdStringDictEntryWithLongKey).key.toString(),
                text: entry.value
            }))
        };
    }, {});
};

export const mapGroupEditRecommendationsToQueryResponse = (
    response: ApolloQueryResult<RecommendationsGroupEditQuery>
): Record<string, EditPageRecommendationsData> => {
    const adGroupsRowset = response.data.client.adGroups.rowset;

    return adGroupsRowset.reduce(
        (
            result: Record<string, EditPageRecommendationsData>,
            row: RecommendationsAdsGroupEditQuery_client_adGroups_rowset
        ) => {
            const { recommendations, id, type } = row;

            if (recommendations && recommendations.length) {
                result[id] = {
                    recommendations: recommendations
                        .filter(recommendation =>
                            isRecommendationSupportedOnEditGroupPage(
                                recommendation.type,
                                type
                            )
                        )
                        .map(mapBaseRecommendationInfoFromApiFormat),
                    isRecommendationsVisible: true
                };
            }

            return result;
        },
        {}
    );
};
